import { ExpandableFabButton } from "../commons/components/buttons/ExpandableFabButton";
import Form from "../commons/components/form/Form";
import { required } from "../commons/model/form/Validators";
import TextAreaInput from "../commons/components/form/input/TextAreaInput";
import * as React from "react";
import { RefObject } from "react";
import InputState from "../commons/model/form/InputState";
import TelInput from "../commons/components/form/input/TelInput";
import sfetch from "../commons/utils/super-fetch";
import Country from "../commons/model/address/Country";
import { CustomErrors } from "../commons/model/form/Field";

interface BookingRequestFormProps {
  bookingApiUrl: string;
  patientHasRequestedBooking: boolean;
  countries: Country[];
  cellPhoneNumber?: string;
  doctorBookingSystemUrl?: string;
}

interface BookingRequestFormState {
  globalErrorMsg?: string;
  patientHasRequestedBooking: boolean;
  showConfirmation: boolean;
  canCollapse: boolean;
  onBooking: boolean;
  phoneInputCustomError: CustomErrors;
}

export class BookingRequestForm extends React.Component<
  BookingRequestFormProps,
  BookingRequestFormState
> {
  private readonly fabButtonRef: RefObject<ExpandableFabButton>;

  constructor(props: BookingRequestFormProps) {
    super(props);

    this.state = {
      patientHasRequestedBooking: this.props.patientHasRequestedBooking,
      showConfirmation: false,
      canCollapse: true,
      onBooking: false,
      phoneInputCustomError: undefined
    };

    this.fabButtonRef = React.createRef();

    this.onCollapse = this.onCollapse.bind(this);
    this.requestAppointment = this.requestAppointment.bind(this);
    this.onBookingRequested = this.onBookingRequested.bind(this);
  }

  render() {
    const bookBtnEnabled =
      this.props.doctorBookingSystemUrl ||
      !(this.state.patientHasRequestedBooking || this.state.onBooking);
    const showConfirmation = this.state.showConfirmation;
    return (
      <div className="appointment-booking">
        {bookBtnEnabled && (
          <>
            <a
              href={this.props.doctorBookingSystemUrl}
              target="_blank"
              className={`button-primary booking-button${bookBtnEnabled ? "" : " hidden"}`}
              onClick={() => {
                if (
                  this.props.doctorBookingSystemUrl &&
                  this.props.doctorBookingSystemUrl.length > 0
                ) {
                  return sfetch(this.props.bookingApiUrl, { method: "PUT" })
                    .catch((res) => {
                      if (res instanceof Response) {
                        if (res.status === 403) {
                          document.location.reload();
                        }
                        if (res.status === 400) {
                          this.setState({
                            ...this.state,
                            phoneInputCustomError: new CustomErrors(true, i18n["Form.Error.InvalidPhoneNumber"])
                          })
                        }
                      }
                    });
                } else {
                  this.fabButtonRef.current.expand();
                  this.setState({
                    ...this.state,
                    onBooking: true,
                  });
                }
              }}
            >
              <i className="ic_calendar" />
              <span className="full-label">
                {i18n["Page.AssessmentResult.Action.BookAnAppointment"]}
              </span>
              <span className="short-label">
                {i18n["Page.AssessmentResult.Action.BookAnAppointment.Short"]}
              </span>
            </a>
            {/*Used to add space with the same height as the booking button*/}
            <div className="button-primary booking-button spacer">
              <i className="ic_calendar" />
              <span>{i18n["Page.AssessmentResult.Action.BookAnAppointment"]}</span>
            </div>
          </>
        )}
        <ExpandableFabButton
          ref={this.fabButtonRef}
          className={!bookBtnEnabled ? "" : "hidden"}
          icon="ic_rdv_booked"
          banner="ic_calendar"
          title={showConfirmation ? null : i18n["Page.AssessmentResult.Action.BookAnAppointment"]}
          canCollapse={this.state.canCollapse}
          onCollapse={this.onCollapse}
        >
          {this.renderBookingRequestForm()}
        </ExpandableFabButton>
      </div>
    );
  }

  componentDidUpdate(
    prevProps: Readonly<BookingRequestFormProps>,
    prevState: Readonly<BookingRequestFormState>
  ): void {
    if (this.state.showConfirmation) {
      setTimeout(() => {
        if (this.state.showConfirmation) {
          this.fabButtonRef.current.collapse();
        }
      }, 3000);
    }
  }

  private renderBookingRequestForm() {
    return (
      <>
        {this.state.showConfirmation ? (
          <div className="confirmation">
            <div className="icon-container">
              <i className="ic_check_circled" />
            </div>
            <p className="title">{i18n["Global.ThankYou"]}</p>
            <p className="description">{i18n["Form.BookAnAppointment.Confirmation"]}</p>
          </div>
        ) : (
          <>
            <div className="description">{i18n["Form.BookAnAppointment.Description"]}</div>
            <Form
              submitLabel={i18n["Global.Send"]}
              submit={this.requestAppointment}
              onSubmitSuccess={this.onBookingRequested}
              generalErrorMsg={this.state.globalErrorMsg}
            >
              <TelInput
                name="cellPhoneNumber"
                label={i18n["Form.Field.Title.PhoneNumber.Cell"]}
                placeholder={i18n["Form.BookAnAppointment.Phone.Placeholder"]}
                initialValue={this.props.cellPhoneNumber}
                availableCountries={this.props.countries}
                validators={[required()]}
                customErrors={this.state.phoneInputCustomError}
                dirty={!!this.props.cellPhoneNumber}
              />
              <TextAreaInput
                name="message"
                label={i18n["Form.BookAnAppointment.Message.Title"]}
                placeholder={i18n["Form.BookAnAppointment.Message.Placeholder"]}
                detail={i18n["Global.OptionalField"]}
                maxLength={500}
              />
            </Form>
          </>
        )}
      </>
    );
  }

  private requestAppointment(form: { [key: string]: InputState }) {
    const payload = {
      cellPhoneNumber: form["cellPhoneNumber"].value,
      message: form["message"].value,
    };

    this.setState({
      ...this.state,
      canCollapse: false,
    });
    return sfetch(this.props.bookingApiUrl, {
      method: "PUT",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
    }).then((response) => {
      this.setState({
        ...this.state,
        canCollapse: true,
      });
      return response;
    }).catch((res) => {
      if (res instanceof Response) {
        if(res.status === 403) {
          document.location.reload();
        }
        if (res.status === 400) {
          this.setState({ ...this.state, phoneInputCustomError: new CustomErrors(true, i18n["Form.Error.InvalidPhoneNumber"]) })
        }
      }
      return res;
    })
  }

  private onCollapse() {
    this.setState({
      ...this.state,
      showConfirmation: false,
      onBooking: false,
    });
  }

  private onBookingRequested() {
    this.setState({
      ...this.state,
      patientHasRequestedBooking: true,
      showConfirmation: true,
    });
  }
}
